/**
 * Capabilities, client side.
 *
 * Deliberately *not* a copy of the server's role→permission table. The browser
 * asks `GET /api/auth/capabilities` and renders from the answer, because a
 * second copy of that table would drift the first time somebody edited one —
 * and the failure mode is invisible: a button that renders and then 403s, or one
 * that stays hidden from somebody entitled to it.
 *
 * What lives here is the part a component actually needs: the permission
 * *names*, so a screen can say which capability it requires, and the Arabic
 * labels the hospital admin reads when ticking privileges for a co-admin.
 * `Tests/suites/frontend/rbac-parity.test.mjs` asserts this name list is
 * identical to `backend/lib/rbac.js` — that is the only thing that can drift,
 * and it is one assertion.
 *
 * This mirrors the arrangement `domain/pricing.js` has with `lib/pricing.js`,
 * with one difference worth stating: pricing is duplicated because the browser
 * must *quote* a price before the server stores one. Nothing here is computed
 * before the server has answered, so nothing here needs duplicating.
 */

/** Every capability name the platform recognises. Must match lib/rbac.js. */
export const PERMISSIONS = Object.freeze({
  PLATFORM_ADMIN: 'PLATFORM_ADMIN',

  HOSPITAL_SETTINGS_MANAGE: 'HOSPITAL_SETTINGS_MANAGE',
  HOSPITAL_STAFF_MANAGE: 'HOSPITAL_STAFF_MANAGE',
  HOSPITAL_STAFF_VIEW: 'HOSPITAL_STAFF_VIEW',
  HOSPITAL_FINANCE_VIEW: 'HOSPITAL_FINANCE_VIEW',
  HOSPITAL_SERVICES_MANAGE: 'HOSPITAL_SERVICES_MANAGE',

  APPOINTMENT_VIEW: 'APPOINTMENT_VIEW',
  APPOINTMENT_BOOK: 'APPOINTMENT_BOOK',
  APPOINTMENT_CANCEL: 'APPOINTMENT_CANCEL',
  INTAKE_FIELDS_MANAGE: 'INTAKE_FIELDS_MANAGE',
  SHARE_LINK_ISSUE: 'SHARE_LINK_ISSUE',
  SHARE_LINK_REVOKE: 'SHARE_LINK_REVOKE',
  PAYMENT_SUBMIT: 'PAYMENT_SUBMIT',

  CLINIC_MANAGE: 'CLINIC_MANAGE',
  DOCTOR_ASSIGN: 'DOCTOR_ASSIGN',
  SCHEDULE_CONFIGURE: 'SCHEDULE_CONFIGURE',
  SCHEDULE_SHIFT: 'SCHEDULE_SHIFT',
  QUEUE_OVERSEE: 'QUEUE_OVERSEE',
  PAYMENT_CONFIRM: 'PAYMENT_CONFIRM',

  QUEUE_ADVANCE: 'QUEUE_ADVANCE',
  PATIENT_RECORD_VIEW: 'PATIENT_RECORD_VIEW',
  PATIENT_RECORD_APPEND: 'PATIENT_RECORD_APPEND',
  PRESCRIPTION_WRITE: 'PRESCRIPTION_WRITE',
  CONSULTATION_FINISH: 'CONSULTATION_FINISH',

  PERSONAL_CLINIC_MANAGE: 'PERSONAL_CLINIC_MANAGE',

  PATIENT_FILE_MANAGE: 'PATIENT_FILE_MANAGE',
  INSURANCE_MANAGE: 'INSURANCE_MANAGE',
  ATTACHMENT_MANAGE: 'ATTACHMENT_MANAGE',

  SERVICE_REQUEST_CREATE: 'SERVICE_REQUEST_CREATE',
  SERVICE_REQUEST_APPROVE: 'SERVICE_REQUEST_APPROVE',
  DIAGNOSTICS_APPROVE: 'DIAGNOSTICS_APPROVE',
  SURGERY_CONFIRM_ON_BEHALF: 'SURGERY_CONFIRM_ON_BEHALF',

  ROOMS_MANAGE: 'ROOMS_MANAGE',
  NURSING_CARE: 'NURSING_CARE',
  LAB_WORK: 'LAB_WORK',
  RADIOLOGY_WORK: 'RADIOLOGY_WORK',
  PHARMACY_DISPENSE: 'PHARMACY_DISPENSE',

  BILLING_MANAGE: 'BILLING_MANAGE',
  RECEIPT_ISSUE: 'RECEIPT_ISSUE',

  AUDIT_VIEW: 'AUDIT_VIEW',
  DATA_EXPORT: 'DATA_EXPORT',
  API_KEYS_MANAGE: 'API_KEYS_MANAGE',

  // المرحلة 1.4 — the visit's documentation, each tied to its module.
  ENCOUNTER_DOCUMENT: 'ENCOUNTER_DOCUMENT',
  CERTIFICATE_ISSUE: 'CERTIFICATE_ISSUE',
  CERTIFICATE_APPROVE: 'CERTIFICATE_APPROVE',
  REFERRAL_ISSUE: 'REFERRAL_ISSUE',
  REFERRAL_DIRECTORY_MANAGE: 'REFERRAL_DIRECTORY_MANAGE',
  TERMINOLOGY_MANAGE: 'TERMINOLOGY_MANAGE',

  AI_ANALYST: 'AI_ANALYST',
  AI_PREMIUM_MODEL: 'AI_PREMIUM_MODEL',
});

export const ALL_PERMISSIONS = Object.freeze(Object.values(PERMISSIONS));

/**
 * What each capability is called on the privileges screen.
 *
 * Arabic, because a hospital admin ticking boxes for their deputy is the only
 * person who ever reads these. The English one-liners on the `Permissions` rows
 * are for an operator reading the database.
 */
export const PERMISSION_LABELS = Object.freeze({
  [PERMISSIONS.PLATFORM_ADMIN]: 'إدارة المنصة بالكامل',

  [PERMISSIONS.HOSPITAL_SETTINGS_MANAGE]: 'تعديل بيانات المنشأة وإعداداتها',
  [PERMISSIONS.HOSPITAL_STAFF_MANAGE]: 'إنشاء حسابات الموظفين وإيقافها',
  [PERMISSIONS.HOSPITAL_STAFF_VIEW]: 'الاطلاع على قائمة الموظفين دون تعديلها',
  [PERMISSIONS.HOSPITAL_FINANCE_VIEW]: 'الاطلاع على الحسابات والإيرادات',
  [PERMISSIONS.HOSPITAL_SERVICES_MANAGE]: 'إدارة الخدمات والأسعار',

  [PERMISSIONS.APPOINTMENT_VIEW]: 'عرض حجوزات أطباء المنشأة',
  [PERMISSIONS.APPOINTMENT_BOOK]: 'حجز موعد نيابة عن المريض',
  [PERMISSIONS.APPOINTMENT_CANCEL]: 'إلغاء الحجوزات',
  [PERMISSIONS.INTAKE_FIELDS_MANAGE]: 'إنشاء حقول استقبال مخصصة',
  [PERMISSIONS.SHARE_LINK_ISSUE]: 'إصدار رابط مشاركة للمريض',
  [PERMISSIONS.SHARE_LINK_REVOKE]: 'إلغاء رابط مشاركة',
  [PERMISSIONS.PAYMENT_SUBMIT]: 'تسجيل استلام مبلغ من المريض',

  [PERMISSIONS.CLINIC_MANAGE]: 'إدارة العيادات والغرف',
  [PERMISSIONS.DOCTOR_ASSIGN]: 'توزيع الأطباء على العيادات',
  [PERMISSIONS.SCHEDULE_CONFIGURE]: 'ضبط المواعيد والغرف المتوازية',
  [PERMISSIONS.SCHEDULE_SHIFT]: 'تأجيل مواعيد اليوم وإخطار المرضى',
  [PERMISSIONS.QUEUE_OVERSEE]: 'الإشراف على جميع طوابير الانتظار',
  [PERMISSIONS.PAYMENT_CONFIRM]: 'تأكيد تحصيل المدفوعات',

  [PERMISSIONS.QUEUE_ADVANCE]: 'إدارة طابور طبيب واحد',
  [PERMISSIONS.PATIENT_RECORD_VIEW]: 'الاطلاع على الملف الطبي',
  [PERMISSIONS.PATIENT_RECORD_APPEND]: 'الإضافة إلى الملف الطبي',
  [PERMISSIONS.PRESCRIPTION_WRITE]: 'كتابة وتعديل الروشتات',
  [PERMISSIONS.CONSULTATION_FINISH]: 'إنهاء الكشف وإخطار السكرتارية',

  [PERMISSIONS.PERSONAL_CLINIC_MANAGE]: 'إدارة العيادة الخاصة (عروض، أكواد، باقات)',

  [PERMISSIONS.PATIENT_FILE_MANAGE]: 'تسجيل ملفات المرضى وتعديلها',
  [PERMISSIONS.INSURANCE_MANAGE]: 'ضبط التغطية التأمينية وحدودها',
  [PERMISSIONS.ATTACHMENT_MANAGE]: 'رفع المرفقات وتصنيفها',

  [PERMISSIONS.SERVICE_REQUEST_CREATE]: 'تحويل المريض للأقسام (تحاليل، أشعة، غرف، عمليات)',
  [PERMISSIONS.SERVICE_REQUEST_APPROVE]: 'الموافقة المالية على الطلبات',
  [PERMISSIONS.DIAGNOSTICS_APPROVE]: 'الموافقة المالية على التحاليل والأشعة',
  [PERMISSIONS.SURGERY_CONFIRM_ON_BEHALF]: 'تأكيد العمليات بالنيابة عن الجراح',

  [PERMISSIONS.ROOMS_MANAGE]: 'إدارة الغرف والأسرّة والعمليات والعناية',
  [PERMISSIONS.NURSING_CARE]: 'تسجيل الرعاية التمريضية',
  [PERMISSIONS.LAB_WORK]: 'تنفيذ طلبات المعمل وتسجيل النتائج',
  [PERMISSIONS.RADIOLOGY_WORK]: 'تنفيذ طلبات الأشعة وكتابة التقارير',
  [PERMISSIONS.PHARMACY_DISPENSE]: 'صرف الأدوية',

  [PERMISSIONS.BILLING_MANAGE]: 'تسعير الخدمات وإرسال المستحقات للاستقبال',
  [PERMISSIONS.RECEIPT_ISSUE]: 'إصدار الإيصالات الإلكترونية وطباعتها',

  [PERMISSIONS.AUDIT_VIEW]: 'قراءة سجل الوصول: مين فتح إيه وغيّر إيه',
  [PERMISSIONS.DATA_EXPORT]: 'تصدير الجداول والتقارير CSV وExcel',
  [PERMISSIONS.API_KEYS_MANAGE]: 'إنشاء مفاتيح الربط (API) وإلغاؤها',

  [PERMISSIONS.ENCOUNTER_DOCUMENT]: 'توثيق الزيارة: SOAP والتشخيص والقياسات والإجراءات وتقرير الزيارة',
  [PERMISSIONS.CERTIFICATE_ISSUE]: 'إصدار الشهادات الطبية: إجازة مرضية ولياقة وتقرير',
  [PERMISSIONS.CERTIFICATE_APPROVE]: 'اعتماد الشهادات الطبية قبل تسليمها',
  [PERMISSIONS.REFERRAL_ISSUE]: 'إصدار مستند إحالة لجهة برّه المنشأة',
  [PERMISSIONS.REFERRAL_DIRECTORY_MANAGE]: 'إدارة دليل جهات الإحالة الخاص بالمنشأة',
  [PERMISSIONS.TERMINOLOGY_MANAGE]: 'إدارة أكواد ICD-10 والإجراءات الخاصة بالمنشأة',

  [PERMISSIONS.AI_ANALYST]: 'تحليل الملف الطبي بالذكاء الاصطناعي',
  [PERMISSIONS.AI_PREMIUM_MODEL]: 'استخدام النموذج المدفوع المتقدم',
});

/**
 * The privileges a hospital admin may hand to a co-admin.
 *
 * Mirrors CO_ADMIN_GRANTABLE on the server, which filters the same two names
 * out of any stored bundle. Listed here so the ticking screen does not offer a
 * box that the server would silently drop — a checkbox that unticks itself on
 * save is worse than one that was never there.
 */
export const GRANTABLE_PERMISSIONS = Object.freeze(
  ALL_PERMISSIONS.filter(
    (name) => name !== PERMISSIONS.PLATFORM_ADMIN && name !== PERMISSIONS.AI_PREMIUM_MODEL,
  ),
);

/** How the privileges screen groups the checkboxes. Presentation only. */
export const PERMISSION_GROUPS = Object.freeze([
  {
    label: 'إدارة المنشأة',
    permissions: [
      PERMISSIONS.HOSPITAL_SETTINGS_MANAGE,
      PERMISSIONS.HOSPITAL_STAFF_MANAGE,
      PERMISSIONS.HOSPITAL_STAFF_VIEW,
      PERMISSIONS.HOSPITAL_FINANCE_VIEW,
      PERMISSIONS.HOSPITAL_SERVICES_MANAGE,
    ],
  },
  {
    label: 'الاستقبال والحجوزات',
    permissions: [
      PERMISSIONS.APPOINTMENT_VIEW,
      PERMISSIONS.APPOINTMENT_BOOK,
      PERMISSIONS.APPOINTMENT_CANCEL,
      PERMISSIONS.INTAKE_FIELDS_MANAGE,
      PERMISSIONS.SHARE_LINK_ISSUE,
      PERMISSIONS.SHARE_LINK_REVOKE,
      PERMISSIONS.PAYMENT_SUBMIT,
    ],
  },
  {
    label: 'تشغيل العيادات',
    permissions: [
      PERMISSIONS.CLINIC_MANAGE,
      PERMISSIONS.DOCTOR_ASSIGN,
      PERMISSIONS.SCHEDULE_CONFIGURE,
      PERMISSIONS.SCHEDULE_SHIFT,
      PERMISSIONS.QUEUE_OVERSEE,
      PERMISSIONS.PAYMENT_CONFIRM,
    ],
  },
  {
    label: 'غرفة الكشف',
    permissions: [
      PERMISSIONS.QUEUE_ADVANCE,
      PERMISSIONS.PATIENT_RECORD_VIEW,
      PERMISSIONS.PATIENT_RECORD_APPEND,
      PERMISSIONS.PRESCRIPTION_WRITE,
      PERMISSIONS.CONSULTATION_FINISH,
      PERMISSIONS.AI_ANALYST,
    ],
  },
  {
    label: 'ملف المريض والتأمين',
    permissions: [
      PERMISSIONS.PATIENT_FILE_MANAGE,
      PERMISSIONS.INSURANCE_MANAGE,
      PERMISSIONS.ATTACHMENT_MANAGE,
      PERMISSIONS.SERVICE_REQUEST_CREATE,
      PERMISSIONS.SERVICE_REQUEST_APPROVE,
      PERMISSIONS.DIAGNOSTICS_APPROVE,
      PERMISSIONS.SURGERY_CONFIRM_ON_BEHALF,
    ],
  },
  {
    label: 'توثيق الزيارة ومستنداتها',
    permissions: [
      PERMISSIONS.ENCOUNTER_DOCUMENT,
      PERMISSIONS.CERTIFICATE_ISSUE,
      PERMISSIONS.CERTIFICATE_APPROVE,
      PERMISSIONS.REFERRAL_ISSUE,
      PERMISSIONS.REFERRAL_DIRECTORY_MANAGE,
      PERMISSIONS.TERMINOLOGY_MANAGE,
    ],
  },
  {
    label: 'الأقسام',
    permissions: [
      PERMISSIONS.ROOMS_MANAGE,
      PERMISSIONS.NURSING_CARE,
      PERMISSIONS.LAB_WORK,
      PERMISSIONS.RADIOLOGY_WORK,
      PERMISSIONS.PHARMACY_DISPENSE,
    ],
  },
  {
    label: 'الحسابات والإيصالات',
    permissions: [PERMISSIONS.BILLING_MANAGE, PERMISSIONS.RECEIPT_ISSUE],
  },
  {
    label: 'العيادة الخاصة',
    permissions: [PERMISSIONS.PERSONAL_CLINIC_MANAGE],
  },
  {
    label: 'الامتثال والبيانات',
    permissions: [PERMISSIONS.AUDIT_VIEW, PERMISSIONS.DATA_EXPORT, PERMISSIONS.API_KEYS_MANAGE],
  },
]);

/**
 * Who may open a patient's unified medical file, and who reads its money.
 * Mirrors lib/rbac.js PATIENT_FILE_READERS / PATIENT_FILE_MONEY; the parity
 * test fails if they drift, which would put an "open the file" button on a
 * desk the server refuses — or hide it from one the server serves.
 */
export const PATIENT_FILE_READERS = Object.freeze([
  PERMISSIONS.PATIENT_RECORD_VIEW,
  PERMISSIONS.PATIENT_FILE_MANAGE,
  PERMISSIONS.INSURANCE_MANAGE,
  PERMISSIONS.SERVICE_REQUEST_APPROVE,
  PERMISSIONS.BILLING_MANAGE,
  PERMISSIONS.NURSING_CARE,
]);

export const PATIENT_FILE_MONEY = Object.freeze([
  PERMISSIONS.BILLING_MANAGE,
  PERMISSIONS.SERVICE_REQUEST_APPROVE,
  PERMISSIONS.HOSPITAL_FINANCE_VIEW,
  PERMISSIONS.RECEIPT_ISSUE,
]);

/**
 * The administration roles (المرحلة 1.1, P15): ready-made bundles on an
 * administrative-manager account. Mirrors lib/adminRoles.js on the server,
 * which is what actually stores the bundle; the parity test fails if they
 * drift. The fifth role is the Super Admin, at the platform level.
 */
export const ADMIN_ROLES = Object.freeze({
  operations: Object.freeze([
    PERMISSIONS.HOSPITAL_STAFF_VIEW,
    PERMISSIONS.APPOINTMENT_VIEW,
    PERMISSIONS.APPOINTMENT_BOOK,
    PERMISSIONS.APPOINTMENT_CANCEL,
    PERMISSIONS.CLINIC_MANAGE,
    PERMISSIONS.DOCTOR_ASSIGN,
    PERMISSIONS.SCHEDULE_CONFIGURE,
    PERMISSIONS.SCHEDULE_SHIFT,
    PERMISSIONS.QUEUE_OVERSEE,
    PERMISSIONS.ROOMS_MANAGE,
    PERMISSIONS.DATA_EXPORT,
  ]),
  finance: Object.freeze([
    PERMISSIONS.HOSPITAL_FINANCE_VIEW,
    PERMISSIONS.HOSPITAL_SERVICES_MANAGE,
    PERMISSIONS.PAYMENT_CONFIRM,
    PERMISSIONS.DATA_EXPORT,
  ]),
  support: Object.freeze([PERMISSIONS.HOSPITAL_STAFF_VIEW, PERMISSIONS.HOSPITAL_STAFF_MANAGE, PERMISSIONS.APPOINTMENT_VIEW]),
  compliance: Object.freeze([PERMISSIONS.AUDIT_VIEW, PERMISSIONS.DATA_EXPORT, PERMISSIONS.HOSPITAL_STAFF_VIEW]),
});
export const ADMIN_ROLE_KEYS = Object.freeze(Object.keys(ADMIN_ROLES));

/**
 * The Arabic title of every login role. Mirrors lib/rbac.js ROLE_TITLES, which
 * the server also writes into `Roles.displayName`; rbac-parity.test.mjs fails
 * if the two disagree. The QA report renamed five jobs — the role *names* are
 * unchanged identifiers, only these words moved.
 */
export const ROLE_TITLES = Object.freeze({
  admin: 'مدير المنصة',
  doctor: 'طبيب',
  patient: 'مريض',
  team_member: 'فريق العمل',
  institution: 'مدير المنشأة',
  co_admin: 'مدير إداري',
  receptionist: 'استقبال مرضى',
  operations_manager: 'استقبال العيادات',
  secretary: 'سكرتير طبيب',
  contracts_manager: 'مسؤول التعاقدات والموافقات',
  accounts_manager: 'محاسب',
  rooms_manager: 'مدير الغرف والعمليات والعناية',
  nurse: 'تمريض',
  lab_technician: 'المعمل',
  radiologist: 'الأشعة',
  pharmacist: 'الصيدلية',
  platform_staff: 'فريق المنصة',
});

/** The Arabic name of each hospital staff account type. */
export const MEMBER_ROLE_LABELS = Object.freeze({
  manager: 'مدير المنشأة',
  co_admin: ROLE_TITLES.co_admin,
  receptionist: ROLE_TITLES.receptionist,
  operations_manager: ROLE_TITLES.operations_manager,
  secretary: 'سكرتير طبيب',
  doctor: 'طبيب',
  staff: 'موظف',
  contracts_manager: ROLE_TITLES.contracts_manager,
  accounts_manager: ROLE_TITLES.accounts_manager,
  rooms_manager: 'مدير الغرف والعمليات والعناية',
  nurse: 'تمريض',
  lab_technician: 'المعمل',
  radiologist: 'الأشعة',
  pharmacist: 'الصيدلية',
});

/** The account types a hospital admin may create. Mirrors CREATABLE_MEMBER_ROLES. */
export const CREATABLE_MEMBER_ROLES = Object.freeze([
  'co_admin',
  'receptionist',
  'operations_manager',
  'secretary',
  'doctor',
  'staff',
  'contracts_manager',
  'accounts_manager',
  'rooms_manager',
  'nurse',
  'lab_technician',
  'radiologist',
  'pharmacist',
]);

/**
 * The twelve roles the specification counts, in the order the hospital staff
 * screen offers them, with the icon and tone each role's tile carries. The
 * legacy `staff` is creatable but not one of the twelve.
 */
export const SPEC_MEMBER_ROLES = Object.freeze([
  'co_admin',
  'doctor',
  'secretary',
  'receptionist',
  'operations_manager',
  'contracts_manager',
  'accounts_manager',
  'rooms_manager',
  'nurse',
  'lab_technician',
  'radiologist',
  'pharmacist',
]);

/**
 * May an account holding this login role create, edit, reset or suspend a
 * staff account of this member role? Mirrors lib/hospitalStaff.js
 * staffTargetAllowed — the server enforces it; this only decides which
 * buttons to draw.
 *
 * The administrative manager (co_admin) manages the hospital's staff but not
 * its peers or the owner, so a reset-to-username can never be used to sign in
 * as another administrator.
 */
export const PEER_MEMBER_ROLES = Object.freeze(['manager', 'co_admin']);

export function mayManageStaffRole(actorRole, targetMemberRole) {
  if (actorRole === 'admin' || actorRole === 'institution') return true;
  return !PEER_MEMBER_ROLES.includes(targetMemberRole);
}
